/**
 * BlacklogixCube component module.
 * @module shared/widgets/containers/BlacklogixCube
 */
import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import useSWR from 'swr';
import {Column, DescriptiveText, Dropdown, LoadingSpinner} from '../../../../shared';
import useQueryString from '../../../../shared/utils/useQueryString';
import {_getPeriods, getDefaultTimePeriod} from '../../../../shared/reducers/periods';
import Cube from '../../../../shared/components/graphs/Cube';
import {roundNumber, formatter, parseErrorMessage} from '../../../../shared/utils/utils';

const toCurrency = formatter('currency', {
  noDecimals: true,
});

const rangeStart = 0;
const rangeEndMin = 0.01;

const colors = {
  cubeTitle: 0xF3F3F4,
  legendTitle: 0xF3F3F4,
  legendLabel: 0xD8D8D8,

  left: 0x485055,
  right: 0x303A41,
  bottom: 0x9B9EA2,
  background: 0x1C2427,

  accountGrowth: 0x99FA66,
  investmentGL: 0xFFCC03,
  dividendsInterest: 0x22BEC5,
  advisoryFees: 0xB3B4DE,
};

function buildCylinders(cubeData = {}) {
  const totalAccountGrowth = roundNumber(cubeData.totalAccountGrowth || 0, 0);
  const investmentGainsLosses = roundNumber(cubeData.investmentGainsLosses || 0, 0);
  const dividendInterestGrowth = roundNumber(cubeData.dividendInterestGrowth || 0, 0);
  const advisoryFees = roundNumber(cubeData.advisoryFees || 0, 0);

  const rangeEnd = Math.max(
    rangeEndMin,
    totalAccountGrowth,
    investmentGainsLosses,
    dividendInterestGrowth,
    advisoryFees
  );

  return [
    {
      bottomCaptionText: `Acct. Growth: ${toCurrency(totalAccountGrowth)}`,
      hexColorLegendCaptionText: colors.legendLabel,
      hexColor: colors.accountGrowth,
      value: totalAccountGrowth,
      rangeStart,
      rangeEnd,
    },
    {
      bottomCaptionText: `Investment G/L: ${toCurrency(investmentGainsLosses)}`,
      hexColorLegendCaptionText: colors.legendLabel,
      hexColor: colors.investmentGL,
      value: investmentGainsLosses,
      rangeStart,
      rangeEnd,
    },
    {
      bottomCaptionText: `Div&Int Growth: ${toCurrency(dividendInterestGrowth)}`,
      hexColorLegendCaptionText: colors.legendLabel,
      hexColor: colors.dividendsInterest,
      value: dividendInterestGrowth,
      rangeStart,
      rangeEnd,
    },
    {
      bottomCaptionText: `Advisory Fees: ${toCurrency(advisoryFees)}`,
      hexColorLegendCaptionText: colors.legendLabel,
      hexColor: colors.advisoryFees,
      value: advisoryFees,
      rangeStart,
      rangeEnd,
    },
  ];
}

const BlacklogixCube = ({
  containerID,
  periods,
  getPeriods,
}) => {
  // periods handling (init, query string, and current)
  const [periodID, setPeriodID] = useQueryString('trinlogixPeriod');
  const period = periods.find(o => o.id === periodID);
  React.useEffect(() => getPeriods(), []);

  // fetch container and cube data
  const {data: containerData, error: containerError} = useSWR(containerID && `/containers/${containerID}`);
  const {data: cubeData, error: cubeError} = useSWR(containerID && periodID && `/containers/${containerID}/trinlogix/${periodID}`);

  // init default period
  React.useEffect(() => {
    // do not proceed if periods or container data have not loaded
    // or the period ID has already been set
    if (!periods.length || !containerData || periodID) {
      return;
    }

    // with periods and start date available, determine the default period
    const defaultPeriod = getDefaultTimePeriod(null, containerData.start);
    if (defaultPeriod) {
      setPeriodID(defaultPeriod.id);
    }
  }, [periods, containerData]);

  // process errors if one exists
  const hasHistoryError = !!cubeError && cubeError?.response?.data?.error?.status === 409;
  const hasServerError = !!containerError || (!!cubeError && !hasHistoryError);
  const historyErrorMsg = hasHistoryError ? parseErrorMessage(cubeError) : null;

  // loading indicator
  const isLoading = !periods?.length || (!containerData && !containerError) || (!cubeData && !cubeError);

  // filter, sort, and map to drop down options
  const options = periods
    .filter(o => o.id === 'MAX' || o.start >= containerData?.start)
    .sort((a, b) => {
      if (a.start > b.start) return -1;
      if (a.start < b.start) return 1;
      return 0;
    })
    .map(o => ({
      label: o.name,
      value: o,
    }));

  return (
    <Column
      alignSubRow='stretch'
      alignItems='stretch'
      layout={{grow: 1}}>
      <Dropdown
        title='Time Period'
        placeholder='Select a Period'
        options={options}
        dataPoint={{
          name: 'period',
          value: {
            label: period?.name || '',
            value: period || {},
          },
        }}
        onChange={(name, value) => {
          setPeriodID(value.value.id);
        }}
      />
      <LoadingSpinner show={isLoading} />
      {cubeData && containerData ? (
        <Cube
          leftColor={colors.right}
          rightColor={colors.right}
          bottomColor={colors.left}
          backgroundColor={colors.right}
          views={[
            {
              color: colors.legendTitle,
              cylinders: buildCylinders(cubeData),
            },
          ]}
        />
      ) : null}
      {hasHistoryError ? (
        <DescriptiveText>{historyErrorMsg}</DescriptiveText>
      ) : null}
      {hasServerError ? (
        <DescriptiveText>Unable to load the 3D Performance data.</DescriptiveText>
      ) : null}
    </Column>
  );
};

const mapStateToProps = state => ({
  periods: state.periods,
});

const mapDispatchToProps = (dispatch) => ({
  getPeriods: () => dispatch(_getPeriods()),
});

export default connect(mapStateToProps, mapDispatchToProps)(BlacklogixCube);

BlacklogixCube.propTypes = {
  containerID: PropTypes.number.isRequired,
  getPeriods: PropTypes.func.isRequired,
  periods: PropTypes.array.isRequired,
};

BlacklogixCube.defaultProps = {
  periods: [],
};
